import { useState, useEffect, useCallback } from 'react';
import { Bell, BellRing, BellOff } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { pushSupported, pushPermission, isPushEnabled, enablePush, disablePush, ensureSubscribedIfGranted } from '../lib/push';

/**
 * Compact header control to enable/disable Web Push notifications.
 * Green bell = enabled, grey/red bell = not enabled. Hidden on browsers
 * without push support. Shown to every logged-in user.
 */
export const PushNotificationToggle = () => {
  const [supported] = useState(pushSupported());
  const [enabled, setEnabled] = useState(false);
  const [busy, setBusy] = useState(false);
  const denied = pushPermission() === 'denied';

  useEffect(() => {
    if (!supported) return;
    // Silently re-subscribe if already granted, then reflect state.
    ensureSubscribedIfGranted().finally(async () => {
      setEnabled(await isPushEnabled());
    });
  }, [supported]);

  const handleClick = useCallback(async () => {
    if (busy) return;
    setBusy(true);
    try {
      if (enabled) {
        await disablePush();
        setEnabled(false);
        toast.success('Notifications turned off');
      } else {
        await enablePush();
        setEnabled(true);
        toast.success('Notifications enabled — you\'ll be alerted about defects');
      }
    } catch (e) {
      toast.error(e.message || 'Could not change notification setting');
    } finally {
      setBusy(false);
    }
  }, [busy, enabled]);

  if (!supported) return null;

  const Icon = enabled ? BellRing : denied ? BellOff : Bell;
  return (
    <Button
      variant="ghost"
      size="icon"
      className="relative"
      onClick={handleClick}
      disabled={busy}
      title={enabled ? 'Notifications on — click to turn off' : denied ? 'Notifications blocked in browser settings' : 'Enable defect notifications'}
      data-testid="push-toggle-button"
    >
      <Icon className={`h-4 w-4 ${enabled ? 'text-emerald-600' : denied ? 'text-red-500' : 'text-muted-foreground'}`} />
      <span
        className={`absolute -top-0.5 -right-0.5 h-2.5 w-2.5 rounded-full border-2 border-background ${enabled ? 'bg-emerald-500' : denied ? 'bg-red-500' : 'bg-slate-300'}`}
        data-testid="push-status-dot"
      />
    </Button>
  );
};

export default PushNotificationToggle;
